import { Box, styled, Typography } from '@mui/material';
import classnames from 'classnames';
import Lottie from 'lottie-react';
import { PropsWithChildren, useMemo } from 'react';

import { CaseCountsInfo } from '#entities';
import {
  cloudErrorAnimation,
  currentTimeInTimezone,
  GearIcon,
  ShieldIcon,
  useIsSmallDevice,
} from '#shared';

import type { AppDataErrors } from '../../shared';

const PREFIX = 'TSMED-AppStatus';
const classes = {
  root: `${PREFIX}-root`,
  infoList: `${PREFIX}-infoList`,
  infoText: `${PREFIX}-infoText`,
  icon: `${PREFIX}-icon`,
  errorColor: `${PREFIX}-errorColor`,
  errorInfo: `${PREFIX}-errorInfo`,
  errorMessage: `${PREFIX}-errorMessage`,
};

interface AppStatusProps {
  appDataErrors: AppDataErrors;
  lastUpdateTime: Date;
  countsInfo: CaseCountsInfo;
  renderInRow?: boolean;
}

const Root = styled(Box, {
  shouldForwardProp: prop => prop !== 'renderInRow',
})<{ renderInRow: boolean }>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(0, 1),
  width: '100%',
  overflowX: 'auto',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',

  '&::-webkit-scrollbar': {
    display: 'none',
  },

  [theme.breakpoints.down('lg')]: {
    padding: 0,
  },
}));

const InfoList = styled(Box)(({ theme: { spacing, palette, utils } }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexShrink: 0,
  alignItems: 'center',
  gap: spacing(1),

  [`& .${classes.infoText}`]: {
    margin: 0,
    fontSize: utils.clamp(10, 12),
    color: palette.grey[600],
  },

  [`& .${classes.icon}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    color: palette.grey[600],

    '& svg': {
      width: utils.clamp(12, 14),
      height: utils.clamp(12, 14),
    },
  },

  [`& .${classes.errorColor}`]: {
    color: '#FF3844',
  },
}));

const ErrorInfo = styled(Box)(({ theme: { utils } }) => ({
  display: 'flex',
  gap: 6,
  marginLeft: -4,

  [`& .${classes.errorMessage}`]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: 0,
    fontSize: utils.clamp(12, 20),
    color: '#FF3844',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
}));

/**
 * Component that displays application status information and error state
 */
export const AppStatus = ({
  appDataErrors,
  lastUpdateTime,
  children,
  renderInRow = false,
}: PropsWithChildren<AppStatusProps>) => {
  const hasErrors: boolean = useMemo(
    () => !!Object.keys(appDataErrors).length,
    [appDataErrors],
  );
  const isSmallDevice = useIsSmallDevice();

  return (
    <Root className={classes.root} renderInRow={renderInRow}>
      <InfoList
        className={classes.infoList}
        sx={{
          flexWrap: isSmallDevice ? 'nowrap' : 'wrap',
          minWidth: isSmallDevice ? 'max-content' : 'auto',
        }}
      >
        <Box
          className={classnames(classes.icon, {
            [classes.errorColor]: hasErrors,
          })}
        >
          {hasErrors ? <GearIcon /> : <ShieldIcon />}
          <Typography
            component="p"
            className={classnames(classes.infoText, {
              [classes.errorColor]: hasErrors,
            })}
          >
            Last Update:{' '}
            {currentTimeInTimezone(lastUpdateTime, 'America/New_York')} ET
          </Typography>
        </Box>
      </InfoList>

      {hasErrors && (
        <ErrorInfo className={classes.errorInfo} sx={{ flexShrink: 0 }}>
          <Lottie
            animationData={cloudErrorAnimation}
            loop
            style={{ width: '30px' }}
          />
          <Typography component="span" className={classes.errorMessage}>
            Backend Network Error!
          </Typography>
        </ErrorInfo>
      )}
      {children}
    </Root>
  );
};
